import React from 'react';
import { ChevronRight, Zap } from 'lucide-react';
import { formatDate, hasRealDate } from '../../utils/datetime';
import { FollowupContactBlock } from './FollowupContactBlock';
import { TaskNoteThread } from './TaskNoteThread';
import { VisitProductChips } from '../visits/VisitProductChips';
import { useProducts } from '../../hooks/useOfflineData';
import type { PendingFollowup } from './types';

const MATERIAL_LABELS: Record<string, string> = {
  pricetags: 'Pricetags',
  wobblers: 'Wobblers',
  posters: 'Posters',
  flyers: 'Flyers',
  merch: 'Merch',
};

const ACTION_LABELS: Record<string, string> = {
  meet_decision_maker: 'Meet Decision Maker',
  install_pos: 'Install POS Material',
  check_competitors: 'Check Competitor Activity',
  negotiate_placement: 'Negotiate Better Placement',
  photo_documentation: 'Photo Documentation',
};

function getPriorityColor(priority: string, isOverdue: boolean): string {
  if (isOverdue) return 'bg-red-100 border-red-300 text-red-800';
  switch (priority) {
    case 'urgent':
      return 'bg-fuchsia-50 border-fuchsia-300 text-fuchsia-800';
    case 'high':
      return 'bg-red-50 border-red-200 text-red-700';
    case 'medium':
      return 'bg-orange-50 border-orange-200 text-orange-700';
    default:
      return 'bg-blue-50 border-blue-200 text-blue-700';
  }
}

function dueLabel(dueDate: string): string {
  return hasRealDate(dueDate) ? formatDate(dueDate) : 'No date';
}

interface FollowupCardProps {
  followup: PendingFollowup;
  onOpenPos: (posId: string) => void;
  onQuickVisit: (followup: PendingFollowup) => void;
  onFullVisit: (followup: PendingFollowup) => void;
}

export const FollowupCard: React.FC<FollowupCardProps> = ({
  followup,
  onOpenPos,
  onQuickVisit,
  onFullVisit,
}) => {
  const { products } = useProducts();
  return (
  <div
    className={`bg-white rounded-2xl border-2 overflow-hidden shadow-sm ${
      followup.is_overdue ? 'border-red-300' : 'border-gray-200'
    }`}
  >
    <div className="p-4">
      <CardHeader followup={followup} onOpenPos={onOpenPos} />
      <div className="mb-3">
        <VisitProductChips
          visitProducts={followup.visit_products}
          products={products}
          showSecuredBadge
          placementSecured={followup.placement_secured}
        />
      </div>
      <FollowupContactBlock followup={followup} />
      <MaterialsAndActions followup={followup} />
      <TaskNoteThread
        posId={followup.pos_id}
        visitId={followup.id}
        legacyNotes={followup.followup_notes}
      />
      <ActionButtons
        onQuickVisit={() => onQuickVisit(followup)}
        onFullVisit={() => onFullVisit(followup)}
      />
    </div>
  </div>
  );
};

function CardHeader({
  followup,
  onOpenPos,
}: {
  followup: PendingFollowup;
  onOpenPos: (posId: string) => void;
}) {
  const badge = followup.is_overdue
    ? 'OVERDUE'
    : followup.followup_priority.toUpperCase();

  return (
    <div className="space-y-2 mb-4">
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => onOpenPos(followup.pos_id)}
          className={
            'font-black text-base md:text-lg text-purple-700 ' +
            'hover:text-purple-900 hover:underline text-left flex-1 ' +
            'active:scale-95 transition-all'
          }
        >
          {followup.pos_name}
        </button>
        <span
          className={
            `px-2 py-1 rounded-full text-xs font-bold flex-shrink-0 ` +
            getPriorityColor(followup.followup_priority, followup.is_overdue)
          }
        >
          {badge}
        </span>
      </div>
      <p className="text-xs md:text-sm text-gray-600">{followup.pos_address}</p>
      <DueRow followup={followup} />
    </div>
  );
}

function DueRow({ followup }: { followup: PendingFollowup }) {
  const visitType =
    followup.original_visit_type === 'first_time'
      ? 'First visit'
      : followup.original_visit_type
        ? 'Revisit'
        : null;
  return (
    <div
      className={
        'flex flex-wrap items-center justify-between gap-x-2 gap-y-1 ' +
        'text-xs md:text-sm pt-2 border-t border-gray-100'
      }
    >
      <span
        className={`font-bold ${
          followup.is_overdue ? 'text-red-600' : 'text-gray-700'
        }`}
      >
        Due: {dueLabel(followup.followup_due_date)}
      </span>
      <span className="text-gray-500">
        {followup.days_since_visit} days ago
        {visitType ? ` · ${visitType}` : ''}
      </span>
    </div>
  );
}

function MaterialsAndActions({ followup }: { followup: PendingFollowup }) {
  if (
    followup.followup_materials.length === 0 &&
    followup.followup_actions.length === 0
  ) {
    return null;
  }

  return (
    <div className="space-y-3 mb-4">
      {followup.followup_materials.length > 0 && (
        <TagBlock
          title="Materials to Bring"
          tone="purple"
          items={followup.followup_materials}
          labels={MATERIAL_LABELS}
        />
      )}
      {followup.followup_actions.length > 0 && (
        <TagBlock
          title="Actions Required"
          tone="blue"
          items={followup.followup_actions}
          labels={ACTION_LABELS}
        />
      )}
    </div>
  );
}

function TagBlock({
  title,
  tone,
  items,
  labels,
}: {
  title: string;
  tone: 'purple' | 'blue';
  items: string[];
  labels: Record<string, string>;
}) {
  const wrap =
    tone === 'purple' ? 'bg-purple-50' : 'bg-blue-50';
  const titleCls =
    tone === 'purple' ? 'text-purple-800' : 'text-blue-800';
  const tagCls =
    tone === 'purple'
      ? 'font-bold text-purple-700'
      : 'font-medium text-blue-700';

  return (
    <div className={`p-3 ${wrap} rounded-xl`}>
      <div className={`font-bold text-xs md:text-sm ${titleCls} mb-2`}>
        {title}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <span
            key={item}
            className={`px-2.5 py-1.5 bg-white rounded-lg text-xs ${tagCls}`}
          >
            {labels[item] || item}
          </span>
        ))}
      </div>
    </div>
  );
}

function ActionButtons({
  onQuickVisit,
  onFullVisit,
}: {
  onQuickVisit: () => void;
  onFullVisit: () => void;
}) {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onQuickVisit}
        className={
          'flex-1 px-3 py-3.5 bg-green-600 text-white rounded-xl font-bold ' +
          'hover:bg-green-700 active:scale-95 transition-all ' +
          'flex items-center justify-center gap-2 text-sm md:text-base'
        }
      >
        <Zap size={16} />
        <span>Quick Visit</span>
      </button>
      <button
        type="button"
        onClick={onFullVisit}
        className={
          'flex-1 px-3 py-3.5 bg-blue-600 text-white rounded-xl font-bold ' +
          'hover:bg-blue-700 active:scale-95 transition-all ' +
          'flex items-center justify-center gap-2 text-sm md:text-base'
        }
      >
        <ChevronRight size={16} />
        <span>Full Revisit</span>
      </button>
    </div>
  );
}
